import React, { useCallback } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from 'ucl/themes/shared/primitives';

import ActionButton, { ActionButtonSizes } from 'components/action-button';
import { useCart } from 'hooks/use-cart';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { logButtonClick } from 'state/amplitude';
import { ClickEventComponentNames } from 'state/amplitude/constants';
import { useAuthContext } from 'state/auth';
import { RunningExperiments, useExperiments } from 'state/experiments';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { HapticsNotificationType, hapticNotification } from 'utils/haptic';
import { routes } from 'utils/routing';

import { Badge } from './styled';

export const FloatingViewCartFab = ({ safeAreaBottom = false }) => {
  const { formatMessage } = useIntl();
  const safeAreaBottomInset = useSafeAreaInsets()?.bottom || 0;
  const { isAuthenticated, openSignInModal } = useAuthContext();
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const enableMainFabButton = useFlag(LaunchDarklyFlag.ENABLE_MAIN_FAB_BUTTON);
  const isDesktop = useIsDesktopBreakpoint();
  const { numberOfItemsInCart } = useCart();
  const router = useRouter();
  const { logExposure } = useExperiments();

  const onPress = useCallback(() => {
    logButtonClick(
      {
        attributes: {
          component: ClickEventComponentNames.BUTTON,
          name: 'View Cart',
        },
      },
      { logDuplicateClickEvent: true }
    );

    if (!isAuthenticated) {
      logExposure(RunningExperiments.SignInUpAfterCart);
    }

    if (isAuthenticated || isSignUpAfterCartEnabled) {
      hapticNotification({ type: HapticsNotificationType.SUCCESS });
      router.navigate(routes.cart);
    } else {
      openSignInModal({
        navigateOnSuccessRoute: routes.cart,
      });
    }
  }, [isAuthenticated, isSignUpAfterCartEnabled, router, openSignInModal, logExposure]);

  if (numberOfItemsInCart < 1 || !enableMainFabButton || isDesktop) {
    return null;
  }

  const fabCaption = formatMessage({ id: 'viewCart' });

  return (
    <View
      style={[
        styles.fabContainer,
        { paddingBottom: spacing.$7 + (safeAreaBottom ? safeAreaBottomInset : 0) },
      ]}
    >
      {numberOfItemsInCart && <Badge>{numberOfItemsInCart}</Badge>}
      <ActionButton
        testID="nav-view-cart"
        keepIconSize
        size={ActionButtonSizes.LARGE}
        shadow="7"
        paddingLeft="$6"
        paddingRight="$6"
        backgroundColor={Styles.color.primary}
        _text={{ fontSize: 'lg' }}
        _hover={{
          backgroundColor: Styles.color.primaryHover,
        }}
        onPress={onPress}
        leftIcon={<Icon variant={'cart'} size={'sm'} />}
      >
        {fabCaption}
      </ActionButton>
    </View>
  );
};

const styles = StyleSheet.create({
  fabContainer: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    paddingRight: spacing.$5,
  },
});
